// ==========================================================================
// screens/Family.tsx — the people and the entities that own things
// (FF-025, redesigned in the FF-026 pass 9 Oct 2026).
//   People: a card each in their calendar colour; anyone can change any
//   person's colour on the card (10 Oct: "why is isadora green").
//   Entities: individuals, the trust and companies, with who owns what share
//   drawn as a simple chain (Family trust → 30% → Coretel Group).
//   Recent changes: the audit trail, newest first.
//   Logins (FF-059, 10 Oct: "I want to get Caitlin on the app first"): each
//   adult's card says how they sign in; an admin can give a login by email.
// ==========================================================================
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Building2, Check, KeyRound, Landmark, Loader2, Mail, ShieldCheck, TreeDeciduous, User } from "lucide-react";
import Sheet, { Lbl, inputCls } from "@/components/Sheet";
import { PageHeader, Section } from "@/components/Page";
import ThemeDots from "@/components/ThemeDots";
import { Card } from "@/components/ui";
import { tokenColour } from "@/lib/calendar";
import { PERSON_COLOURS, plainTable, useCancelInvite, useEntities, useEntityOwnerships, useInviteMember, useLogins, useMembership, usePeople, useRecentChanges, useSetPersonColour, type Entity, type Person } from "@/lib/household";

const kindIcon = { individual: User, trust: Landmark, company: Building2 } as const;
const kindLabel = { individual: "Person", trust: "Trust", company: "Company" } as const;

/** Give an adult a login: their email here, then their sign-in in the Supabase dashboard. */
function InviteSheet({ person, onClose }: { person: Person; onClose: () => void }) {
  const invite = useInviteMember();
  const [email, setEmail] = useState(""); const [done, setDone] = useState(false);
  const ok = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  return (
    <Sheet title={`A login for ${person.display_name}`} onClose={onClose}>
      {!done ? (
        <div className="space-y-4">
          <p className="text-sm text-ink-muted">The email {person.display_name} will sign in with. It can be a personal email now and changed later.</p>
          <Lbl label="Email"><input className={inputCls} type="email" inputMode="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" /></Lbl>
          {invite.error && <p className="text-sm text-bad">{(invite.error as Error).message}</p>}
          <button type="button" disabled={!ok || invite.isPending} onClick={() => invite.mutate({ personId: person.id, email: email.trim() }, { onSuccess: () => setDone(true) })}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 font-semibold text-primary-ink disabled:opacity-50">
            {invite.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Save the invite
          </button>
        </div>
      ) : (
        <div className="space-y-4 text-[15px]">
          <p className="flex items-center gap-2 font-semibold text-good"><ShieldCheck className="h-5 w-5" /> Invite saved for {email.trim().toLowerCase()}</p>
          <p>What's left:</p>
          <ol className="list-decimal space-y-2 pl-5">
            <li>Make {person.display_name}'s sign-in in the Supabase dashboard (Authentication → Users → Add user), with this same email and a password, ticking Auto Confirm.</li>
            <li>{person.display_name} signs in at the app with that email and password.</li>
            <li>The first time, {person.display_name} taps "Add to my authenticator" on the phone (or scans the QR code from a computer), then types the 6-digit code. After that, the family is there.</li>
          </ol>
          <button type="button" onClick={onClose} className="w-full rounded-md bg-surface-raised px-4 py-3 font-semibold">Done</button>
        </div>
      )}
    </Sheet>
  );
}

export default function Family() {
  const people = usePeople(); const entities = useEntities(); const owns = useEntityOwnerships(); const changes = useRecentChanges(12);
  const ent = (id: string) => entities.data?.find((e) => e.id === id);
  const logins = useLogins(); const mine = useMembership(); const cancel = useCancelInvite(); const recolour = useSetPersonColour();
  const admin = mine.data?.role === "admin";
  const [inviting, setInviting] = useState<Person | null>(null);
  const nonPeople = (entities.data ?? []).filter((e) => e.kind !== "individual");

  return (
    <>
      <PageHeader title="Family" subtitle="Who's in the household, and the entities that own things. Ownership decides whose tax a cost or income belongs to." />
      <Card className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div><div className="font-semibold">Your colours</div><div className="text-sm text-ink-muted">Just for your login. Caitlin picks her own.</div></div>
        <ThemeDots onPage className="gap-4" />
      </Card>

      <Link to="/family/tree" className="group mb-8 flex items-center gap-5 overflow-hidden rounded-2xl p-6 shadow-soft ring-1 ring-line/70 transition hover:-translate-y-0.5" style={{ background: "radial-gradient(ellipse at 90% 120%, rgb(var(--chart-3) / 0.22), transparent 60%), rgb(var(--surface))" }}>
        <TreeDeciduous className="h-14 w-14 shrink-0 text-chart-3" strokeWidth={1.3} />
        <div className="min-w-0 flex-1"><div className="font-hand text-4xl leading-none text-chart-3">Our family tree</div><div className="mt-1 text-sm text-ink-muted">Parents, grandparents, cousins: build it out over the years. Drawn as a tree or as boxes and lines.</div></div>
        <ArrowRight className="h-5 w-5 shrink-0 text-ink-muted transition group-hover:translate-x-1" />
      </Link>

      <Section title="People" className="mb-10">
        <div className="grid gap-4 sm:grid-cols-3">
          {people.data?.map((p) => (
            <div key={p.id} className="overflow-hidden rounded-lg bg-surface shadow-soft ring-1 ring-line/70">
              <div className="h-2" style={{ background: tokenColour(p.colour) }} />
              <div className="flex items-center gap-4 p-5">
                <span className="flex h-14 w-14 items-center justify-center rounded-full font-heading text-xl font-semibold text-white" style={{ background: tokenColour(p.colour) }}>{p.display_name[0]}</span>
                <div className="min-w-0">
                  <div className="text-lg font-semibold">{p.display_name}</div>
                  <div className="text-sm text-ink-muted">{p.role === "child" ? "Child · no login" : `Adult · WFH day ${p.wfh_default_hours} h`}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 border-t border-line px-5 py-2.5" role="radiogroup" aria-label={`${p.display_name}'s colour`}>
                <span className="mr-auto text-xs text-ink-muted">Colour</span>
                {PERSON_COLOURS.map(({ key, label }) => (
                  <button key={key} type="button" role="radio" aria-checked={p.colour === key} aria-label={label} title={label}
                    onClick={() => p.colour !== key && recolour.mutate({ id: p.id, colour: key })}
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-white ring-offset-2 ring-offset-surface transition ${p.colour === key ? "ring-2 ring-ink" : "hover:scale-110"}`} style={{ background: tokenColour(key) }}>
                    {p.colour === key && <Check className="h-3 w-3" strokeWidth={3} />}
                  </button>
                ))}
              </div>
              {p.role === "adult" && (() => {
                const l = logins.data?.find((x) => x.person_id === p.id);
                return (
                  <div className="flex items-center gap-2 border-t border-line px-5 py-3 text-sm">
                    {l?.accepted_at ? (
                      <><KeyRound className="h-4 w-4 shrink-0 text-good" /><span className="min-w-0 flex-1 truncate">Signs in as {l.invited_email}</span>{l.role === "admin" && <span className="rounded-full bg-surface-raised px-2 py-0.5 text-xs text-ink-muted">Admin</span>}</>
                    ) : l ? (
                      <><Mail className="h-4 w-4 shrink-0 text-warn" /><span className="min-w-0 flex-1"><span className="block truncate">{l.invited_email}</span><span className="block text-xs text-warn">Invited · not signed in yet</span></span>{admin && <button type="button" onClick={() => cancel.mutate(l.id)} className="text-xs font-semibold text-ink-muted hover:text-bad">Cancel</button>}</>
                    ) : admin ? (
                      <button type="button" onClick={() => setInviting(p)} className="flex items-center gap-2 font-semibold text-primary"><KeyRound className="h-4 w-4" /> Give {p.display_name} a login</button>
                    ) : (
                      <span className="text-ink-muted">No login yet</span>
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      </Section>

      <div className="grid gap-10 lg:grid-cols-[1fr_380px]">
        <Section title="Entities and ownership">
          <div className="space-y-4">
            {nonPeople.map((e: Entity) => {
              const Icon = kindIcon[e.kind];
              const ownsOf = (owns.data ?? []).filter((o) => o.owner_entity_id === e.id);
              const ownedBy = (owns.data ?? []).filter((o) => o.owned_entity_id === e.id);
              return (
                <Card key={e.id} className="space-y-3">
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-surface-raised text-ink"><Icon className="h-5 w-5" /></span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-lg font-semibold">{e.name}</span>
                        <span className="rounded-full bg-surface-raised px-2 py-0.5 text-xs text-ink-muted">{kindLabel[e.kind]}</span>
                        {e.is_external && <span className="rounded-full bg-warn/10 px-2 py-0.5 text-xs font-medium text-warn">External</span>}
                      </div>
                      {e.notes && <p className="mt-1 text-sm text-ink-muted">{e.notes}</p>}
                    </div>
                  </div>
                  {ownedBy.map((o) => (
                    <p key={o.id} className="text-sm text-ink-muted">{o.share_pct ?? "?"}% held by <span className="font-medium text-ink">{ent(o.owner_entity_id)?.name}</span></p>
                  ))}
                  {ownsOf.map((o) => (
                    <div key={o.id} className="flex flex-wrap items-center gap-2 rounded-md bg-surface-raised px-3 py-2 text-sm">
                      <span className="font-medium">{ent(o.owner_entity_id)?.name}</span>
                      <ArrowRight className="h-4 w-4 text-ink-muted" />
                      <span className="num rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-white">{o.share_pct ?? "?"}%</span>
                      <span className="text-ink-muted">{o.interest}</span>
                      <ArrowRight className="h-4 w-4 text-ink-muted" />
                      <span className="font-medium">{ent(o.owned_entity_id)?.name}</span>
                      <span className="ml-auto text-xs text-ink-muted">{o.valid_from ? `from ${format(new Date(o.valid_from), "d MMM yyyy")}` : "start date to confirm"}</span>
                    </div>
                  ))}
                </Card>
              );
            })}
          </div>
        </Section>

        <Section title="Recent changes">
          <Card className="p-0">
            <ul className="divide-y divide-line text-sm">
              {changes.data?.map((a) => (
                <li key={a.id} className="flex justify-between gap-3 px-5 py-2.5">
                  <span><span className="capitalize">{a.action}</span> <span className="text-ink-muted">· {plainTable(a.table_name)}</span></span>
                  <span className="num shrink-0 text-ink-muted">{format(new Date(a.at), "d MMM HH:mm")}</span>
                </li>
              ))}
            </ul>
          </Card>
        </Section>
      </div>
      {inviting && <InviteSheet person={inviting} onClose={() => setInviting(null)} />}
    </>
  );
}
